/**
 * Lector mínimo de .xlsx para la importación de productos. Funciones puras
 * salvo por DOMParser y DecompressionStream, que son del navegador.
 *
 * Un .xlsx es un zip con XML adentro. El proyecto no trae ninguna librería
 * de hojas de cálculo (el escritor de informes también está hecho a mano en
 * event-report-xlsx.ts), así que aquí va lo justo para leer la primera hoja
 * con datos: el directorio central del zip, inflar las entradas con
 * DecompressionStream, las cadenas compartidas y las celdas de la hoja.
 * No lee .xls (formato binario viejo): para esos se pide guardar como .xlsx.
 */

export interface XlsxSheetRows {
    sheet: string;
    rows: string[][];
}

// ═══════════════════════════════════════════════════════════════════════════
//  ZIP
// ═══════════════════════════════════════════════════════════════════════════

interface ZipEntry {
    name: string;
    method: number;
    compressedSize: number;
    localOffset: number;
}

const SIG_EOCD = 0x06054b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;

function readEntries(view: DataView): ZipEntry[] {
    // El registro de fin de directorio está al final; puede llevar un
    // comentario detrás, por eso se busca hacia atrás.
    let eocd = -1;
    for (let i = view.byteLength - 22; i >= Math.max(0, view.byteLength - 66000); i--) {
        if (view.getUint32(i, true) === SIG_EOCD) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0) throw new Error('El archivo no es un .xlsx válido.');

    const count = view.getUint16(eocd + 10, true);
    let offset = view.getUint32(eocd + 16, true);
    const entries: ZipEntry[] = [];
    const decoder = new TextDecoder('utf-8');

    for (let i = 0; i < count; i++) {
        if (view.getUint32(offset, true) !== SIG_CENTRAL) break;
        const method = view.getUint16(offset + 10, true);
        const compressedSize = view.getUint32(offset + 20, true);
        const nameLength = view.getUint16(offset + 28, true);
        const extraLength = view.getUint16(offset + 30, true);
        const commentLength = view.getUint16(offset + 32, true);
        const localOffset = view.getUint32(offset + 42, true);
        const nameBytes = new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength);
        entries.push({ name: decoder.decode(nameBytes), method, compressedSize, localOffset });
        offset += 46 + nameLength + extraLength + commentLength;
    }
    return entries;
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
    const DS = (window as any).DecompressionStream;
    if (typeof DS !== 'function') {
        throw new Error('Este navegador no puede leer .xlsx. Guarda el archivo como CSV o usa Chrome/Edge actualizado.');
    }
    // Los tipos del TypeScript de este proyecto no conocen Blob.stream().
    const stream = (new Blob([bytes]) as any).stream().pipeThrough(new DS('deflate-raw'));
    const buffer = await new Response(stream).arrayBuffer();
    return new Uint8Array(buffer);
}

async function readEntry(view: DataView, entry: ZipEntry): Promise<Uint8Array> {
    const at = entry.localOffset;
    if (view.getUint32(at, true) !== SIG_LOCAL) throw new Error('El archivo .xlsx está dañado.');
    const nameLength = view.getUint16(at + 26, true);
    const extraLength = view.getUint16(at + 28, true);
    const start = view.byteOffset + at + 30 + nameLength + extraLength;
    const data = new Uint8Array(view.buffer, start, entry.compressedSize);

    if (entry.method === 0) return data;
    if (entry.method === 8) return inflateRaw(data);
    throw new Error('El .xlsx usa una compresión que no se puede leer.');
}

// ═══════════════════════════════════════════════════════════════════════════
//  SPREADSHEETML
// ═══════════════════════════════════════════════════════════════════════════

function parseXml(text: string): Document {
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length > 0) {
        throw new Error('No se pudo leer el contenido del .xlsx.');
    }
    return doc;
}

function elements(parent: Document | Element, localName: string): Element[] {
    return Array.prototype.slice.call(parent.getElementsByTagNameNS('*', localName));
}

/** "B" → 1, "AA" → 26. */
function columnIndex(ref: string): number {
    const letters = ref.replace(/[^A-Z]/gi, '').toUpperCase();
    let n = 0;
    for (let i = 0; i < letters.length; i++) n = n * 26 + (letters.charCodeAt(i) - 64);
    return Math.max(0, n - 1);
}

function textOf(el: Element): string {
    return elements(el, 't').map(t => t.textContent || '').join('');
}

function cellValue(cell: Element, shared: string[]): string {
    const type = cell.getAttribute('t') || '';
    if (type === 'inlineStr') {
        const is = elements(cell, 'is')[0];
        return is ? textOf(is) : '';
    }
    const v = elements(cell, 'v')[0];
    const raw = v ? (v.textContent || '') : '';
    if (type === 's') {
        const index = parseInt(raw, 10);
        return isNaN(index) ? '' : (shared[index] || '');
    }
    if (type === 'b') return raw === '1' ? 'true' : 'false';
    return raw;
}

function sheetRows(doc: Document, shared: string[]): string[][] {
    const rows: string[][] = [];
    elements(doc, 'row').forEach(rowEl => {
        const row: string[] = [];
        elements(rowEl, 'c').forEach(cell => {
            const ref = cell.getAttribute('r') || '';
            const index = ref ? columnIndex(ref) : row.length;
            while (row.length < index) row.push('');
            row[index] = cellValue(cell, shared).trim();
        });
        rows.push(row);
    });
    return rows.filter(r => r.some(v => v !== ''));
}

/** Normaliza rutas de relaciones: "worksheets/sheet1.xml" o "/xl/worksheets/sheet1.xml". */
function sheetPath(target: string): string {
    const clean = String(target || '').replace(/^\//, '');
    return clean.indexOf('xl/') === 0 ? clean : 'xl/' + clean;
}

/**
 * Devuelve la primera hoja que tenga datos. Las filas vienen ya recortadas
 * (sin filas vacías) y con celdas vacías rellenas con '' para que todas las
 * columnas queden alineadas.
 */
export async function readXlsxRows(buffer: ArrayBuffer): Promise<XlsxSheetRows> {
    const view = new DataView(buffer);
    const entries = readEntries(view);
    const byName: { [name: string]: ZipEntry } = {};
    entries.forEach(e => byName[e.name] = e);

    const text = async (name: string): Promise<string> => {
        const entry = byName[name];
        if (!entry) return '';
        return new TextDecoder('utf-8').decode(await readEntry(view, entry));
    };

    const workbookXml = await text('xl/workbook.xml');
    if (!workbookXml) throw new Error('El archivo no es un libro de Excel (.xlsx).');

    // Cadenas compartidas: cada <si> puede traer varios <t> (texto enriquecido).
    const shared: string[] = [];
    const sharedXml = await text('xl/sharedStrings.xml');
    if (sharedXml) {
        elements(parseXml(sharedXml), 'si').forEach(si => shared.push(textOf(si)));
    }

    // Hojas en el orden del libro, resueltas por su relación.
    const rels: { [id: string]: string } = {};
    const relsXml = await text('xl/_rels/workbook.xml.rels');
    if (relsXml) {
        elements(parseXml(relsXml), 'Relationship').forEach(rel => {
            rels[rel.getAttribute('Id') || ''] = rel.getAttribute('Target') || '';
        });
    }
    const sheets = elements(parseXml(workbookXml), 'sheet').map((sheet, i) => {
        const rId = sheet.getAttribute('r:id') || sheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') || '';
        return {
            name: sheet.getAttribute('name') || `Hoja ${i + 1}`,
            path: rels[rId] ? sheetPath(rels[rId]) : `xl/worksheets/sheet${i + 1}.xml`,
        };
    });

    for (const sheet of sheets) {
        const xml = await text(sheet.path);
        if (!xml) continue;
        const rows = sheetRows(parseXml(xml), shared);
        if (rows.length > 0) return { sheet: sheet.name, rows };
    }
    return { sheet: sheets.length ? sheets[0].name : 'Hoja 1', rows: [] };
}
